import React, { useState, useMemo, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip, TablePagination, TextField, TableSortLabel, Box, InputAdornment, CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { beautifyNumber } from '../../../../Utils/FormatNumber';
import { isAssetSold, isAssetSoldInYear, formatAssetDescription } from '../../../../Utils/assetSaleDisplayUtils';
import useCapitalGainYearSummary from '../../../../hooks/useCapitalGainYearSummary';
import { CapitalGainAmount, CapitalGainStatusChip } from './CapitalGainSummaryCells';
import {
    getSoldAssetsForDisplay,
    buildAssetGroupMap,
    buildSoldAssetRelationMaps,
    getTopLevelSoldAssets,
    filterTopLevelSoldWithChildSearch,
    SOLD_ASSET_ROOT_GROUP_BG,
    SOLD_ASSET_CHILD_ROW_BG,
    SOLD_ASSET_ROW_HOVER_BG,
} from '../../../../Utils/soldAssetsTableUtils';
import AssetModalShell from '../AssetModalShell/AssetModalShell';
import shell from '../AssetModalShell/AssetModalShell.module.scss';
import styles from './CapitalGainSoldAssetsModal.module.scss';

const ALL_YEARS = 'all';

const getSaleYear = (asset) => {
    if (!asset?.sellDate) return null;
    const year = new Date(asset.sellDate).getFullYear();
    return Number.isNaN(year) ? null : year;
};

function descendingComparator(a, b, orderBy) {
    if (a[orderBy] < b[orderBy]) return 1;
    if (a[orderBy] > b[orderBy]) return -1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
        const ord = comparator(a[0], b[0]);
        if (ord !== 0) return ord;
        return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
}

const formatDateShort = (d) =>
    !d ? '-' : new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

const columnsDef = (t) => [
    { id: 'assetNumber', width: '6%', label: t('AssetNumber') || 'מספר נכס' },
    { id: 'name', width: '13%', label: t('AssetName') || 'שם נכס' },
    { id: 'purchaseDate', width: '9%', label: t('PurchaseDate') || 'תאריך רכישה' },
    { id: 'originalPrice', width: '8%', label: t('OriginalPrice') || 'מחיר מקורי' },
    { id: 'sellDate', width: '9%', label: t('SellDate') || 'תאריך מכירה' },
    { id: 'sellAmount', width: '8%', label: t('SellAmount') || 'סכום מכירה' },
    { id: 'gainBeforeTax', width: '10%', label: t('GainBeforeTax') || 'רווח (הפסד) לפני מס', sortable: false },
    { id: 'totalTax', width: '8%', label: t('TaxAmountDue') || 'סכום המס לתשלום', sortable: false },
    { id: 'capitalGainStatus', width: '11%', label: t('CapitalGainStatus') || 'סטטוס', sortable: false },
    { id: 'assetGroupId', width: '8%', label: t('AssetGroup') || 'קבוצה' },
    { id: 'description', width: '10%', label: t('Description') || 'הערות' },
];

/** Child rows: arrow + name in Asset Name; asset number stays in its own column. */
function formatChildAssetNameLabel(asset, isRtl) {
    const marker = isRtl ? '↳' : '↲';
    const name = (asset?.name || '').trim();
    return name ? `${marker} ${name}` : marker;
}

function SoldAssetTableCells({
    asset,
    groupMap,
    t,
    isChildRow = false,
    isRtl = true,
    summary = null,
    showSummary = false,
}) {
    const isLinkedChildSale = Boolean(asset.soldViaAssetGroup) || isChildRow;
    const isMissing = !isLinkedChildSale && (!asset.sellAmount || asset.sellAmount === 0);
    const displayDescription = formatAssetDescription(asset.description, asset.sellDate, isAssetSold(asset));

    return (
        <>
            <TableCell
                align="center"
                sx={isChildRow ? { color: '#666', fontSize: 13 } : undefined}
            >
                {asset.assetNumber ?? '-'}
            </TableCell>
            <TableCell
                align={isChildRow ? 'right' : 'center'}
                sx={
                    isChildRow
                        ? {
                              color: '#666',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                          }
                        : undefined
                }
            >
                <Tooltip title={asset.name || ''} disableHoverListener={!asset.name}>
                    <div className={styles.ellipsis}>
                        {isChildRow ? formatChildAssetNameLabel(asset, isRtl) : (asset.name ?? '-')}
                    </div>
                </Tooltip>
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666' } : undefined}>
                {formatDateShort(asset.purchaseDate)}
            </TableCell>
            <TableCell align="center" className={styles.num} sx={isChildRow ? { color: '#666' } : undefined}>
                {beautifyNumber(asset.originalPrice, 0)}
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666' } : undefined}>
                {formatDateShort(asset.sellDate)}
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666' } : undefined}>
                {isLinkedChildSale ? (
                    '—'
                ) : isMissing ? (
                    <Tooltip title={t('MissingSellAmount')} arrow>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                color: '#d32f2f',
                            }}
                        >
                            <WarningAmberIcon sx={{ fontSize: 16 }} />
                            <span>{t('Missing')}</span>
                        </Box>
                    </Tooltip>
                ) : (
                    beautifyNumber(asset.sellAmount, 0)
                )}
            </TableCell>
            {['gainBeforeTax', 'totalTax'].map((field) => (
                <TableCell key={field} align="center" className={styles.num} sx={{ fontWeight: 600 }}>
                    {showSummary && !isLinkedChildSale ? <CapitalGainAmount summary={summary} field={field} /> : '—'}
                </TableCell>
            ))}
            <TableCell align="center">
                {showSummary && !isLinkedChildSale && <CapitalGainStatusChip summary={summary} t={t} />}
                {showSummary && isLinkedChildSale && !isChildRow && (
                    <span className={styles.linked}>{t('CapitalGainSoldWithParent') || 'נכלל בנכס האב'}</span>
                )}
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666' } : undefined}>
                <div className={styles.clamp}>{groupMap[asset.assetGroupId] || '-'}</div>
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666', fontSize: 12 } : undefined}>
                <Tooltip title={displayDescription || ''} disableHoverListener={!displayDescription}>
                    <div className={styles.clamp}>{displayDescription || '-'}</div>
                </Tooltip>
            </TableCell>
        </>
    );
}

const CapitalGainSoldAssetsModal = ({
    open = false,
    onClose = () => {},
    onSelectAsset = () => {},
    assets = [],
    assetGroups = [],
    assetRelations = [],
    caseId = null,
    token = null,
    caseType = null,
    year = null,
}) => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.dir() === 'rtl';

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('sellDate');
    const [searchTerm, setSearchTerm] = useState('');
    const [yearFilter, setYearFilter] = useState(year ? String(year) : ALL_YEARS);

    useEffect(() => {
        if (!open) return;
        setYearFilter(year ? String(year) : ALL_YEARS);
        setPage(0);
    }, [open, year]);

    const groupMap = useMemo(() => buildAssetGroupMap(assetGroups), [assetGroups]);
    const columns = useMemo(() => columnsDef(t), [t]);

    const soldAssets = useMemo(
        () => getSoldAssetsForDisplay(assets, assetRelations),
        [assets, assetRelations]
    );

    const { childAssetIdSet, rootToChildren } = useMemo(
        () => buildSoldAssetRelationMaps(soldAssets, assetRelations),
        [soldAssets, assetRelations]
    );

    const topLevelSoldAssets = useMemo(
        () => getTopLevelSoldAssets(soldAssets, childAssetIdSet),
        [soldAssets, childAssetIdSet]
    );

    const saleYears = useMemo(() => {
        const counts = new Map();
        topLevelSoldAssets.forEach((asset) => {
            const saleYear = getSaleYear(asset);
            if (saleYear != null) counts.set(saleYear, (counts.get(saleYear) || 0) + 1);
        });
        if (year && !counts.has(Number(year))) counts.set(Number(year), 0);
        return [...counts.entries()].sort((a, b) => b[0] - a[0]);
    }, [topLevelSoldAssets, year]);

    const activeYear = yearFilter === ALL_YEARS ? null : Number(yearFilter);

    const assetsInYear = useMemo(
        () =>
            activeYear == null
                ? topLevelSoldAssets
                : topLevelSoldAssets.filter((asset) => isAssetSoldInYear(asset, activeYear)),
        [topLevelSoldAssets, activeYear]
    );

    const missingAmountCount = useMemo(
        () => assetsInYear.filter((a) => !a.soldViaAssetGroup && (!a.sellAmount || a.sellAmount === 0)).length,
        [assetsInYear]
    );

    const filtered = useMemo(
        () => filterTopLevelSoldWithChildSearch(assetsInYear, rootToChildren, searchTerm, groupMap),
        [assetsInYear, rootToChildren, searchTerm, groupMap]
    );

    const sorted = useMemo(() => {
        if (!orderBy) return filtered;
        return stableSort(filtered, getComparator(order, orderBy));
    }, [filtered, order, orderBy]);

    const paginated = useMemo(
        () => sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
        [sorted, page, rowsPerPage]
    );

    const showSummary = Boolean(caseId && token);
    const computableRows = useMemo(() => paginated.filter((asset) => !asset.soldViaAssetGroup), [paginated]);

    const {
        summaries,
        snapshots,
        saving,
        saveAssets,
        resetAssets,
    } = useCapitalGainYearSummary({
        caseId,
        token,
        caseType,
        assets: computableRows,
        enabled: open && showSummary,
    });

    const yearAssets = useMemo(
        () => (activeYear == null ? [] : assetsInYear.filter((asset) => !asset.soldViaAssetGroup)),
        [assetsInYear, activeYear]
    );
    const savedInYearCount = yearAssets.filter((asset) => snapshots[asset.id]).length;

    const handleSaveYear = async () => {
        try {
            const { saved, failed } = await saveAssets(yearAssets);
            if (saved === 0 && failed === 0) {
                toast.info(t('CapitalGainYearAlreadySaved', { year: activeYear }) || 'כל הנכסים של השנה כבר שמורים');
            } else if (saved > 0) {
                toast.success(t('CapitalGainSnapshotsSaved', { count: saved }) || 'תמונת המצב נשמרה');
            }
            if (failed > 0) {
                toast.warning(t('CapitalGainSnapshotsFailed', { count: failed }) || 'חלק מהנכסים לא חושבו');
            }
        } catch (error) {
            console.error('Failed to save capital gain snapshots:', error);
            toast.error(t('FailedToSaveCapitalGainSnapshot') || 'שגיאה בשמירת תמונת המצב');
        }
    };

    const handleResetYear = async () => {
        const confirm = await Swal.fire({
            title: t('CapitalGainResetYearTitle', { year: activeYear }) || `איפוס רווח הון לשנת ${activeYear}`,
            text: t('CapitalGainResetYearText') || 'הנתונים השמורים יימחקו והערכים יחושבו מחדש מנתוני הנכסים.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('CapitalGainResetConfirm') || 'כן, אפס',
            cancelButtonText: t('Cancel') || 'ביטול',
            reverseButtons: true,
        });
        if (!confirm.isConfirmed) return;
        try {
            await resetAssets({ year: activeYear });
            toast.success(t('CapitalGainSnapshotsReset') || 'הנתונים אופסו לחישוב');
        } catch (error) {
            console.error('Failed to reset capital gain snapshots:', error);
            toast.error(t('FailedToResetCapitalGainSnapshot') || 'שגיאה באיפוס הנתונים');
        }
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    if (!open) return null;

    return (
        <AssetModalShell
            open={open}
            onClose={onClose}
            title={t('CapitalGainSelectSoldAsset') || 'הפקת טופס רווח הון — בחר נכס שנמכר'}
            direction={isRtl ? 'rtl' : 'ltr'}
            size="xl"
            closeOnBackdrop
            toolbar={(
                <div className={shell.toolbar}>
                    <div className={styles.toolbarStart}>
                    <div className={styles.yearPills} role="tablist" aria-label={t('SellDate') || 'תאריך מכירה'}>
                        {saleYears.map(([saleYear, count]) => (
                            <button
                                key={saleYear}
                                type="button"
                                role="tab"
                                aria-selected={activeYear === saleYear}
                                className={`${styles.yearPill} ${activeYear === saleYear ? styles.yearPillActive : ''}`}
                                onClick={() => {
                                    setYearFilter(String(saleYear));
                                    setPage(0);
                                }}
                            >
                                {saleYear}
                                <span className={styles.yearCount}>({count})</span>
                            </button>
                        ))}
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeYear == null}
                            className={`${styles.yearPill} ${activeYear == null ? styles.yearPillActive : ''}`}
                            onClick={() => {
                                setYearFilter(ALL_YEARS);
                                setPage(0);
                            }}
                        >
                            {t('AllYears') || 'כל השנים'}
                            <span className={styles.yearCount}>({topLevelSoldAssets.length})</span>
                        </button>
                    </div>
                    <TextField
                        placeholder={t('Search') || 'חיפוש'}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                        }}
                        variant="standard"
                        className={shell.searchInput}
                        InputProps={{
                            disableUnderline: true,
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        }}
                        inputProps={{ style: { textAlign: isRtl ? 'right' : 'left' } }}
                    />
                    </div>
                    <div className={`${shell.stats} ${shell.toolbarEnd}`}>
                        <span className={shell.stat}>
                            {t('TotalSold') || 'סה"כ נמכרו'}: <strong>{assetsInYear.length}</strong>
                        </span>
                        {showSummary && activeYear != null && (
                            <span className={shell.stat}>
                                {t('CapitalGainSavedInYear', { year: activeYear }) || `שמורים בשנת ${activeYear}`}:{' '}
                                <strong>{savedInYearCount}/{yearAssets.length}</strong>
                            </span>
                        )}
                        {missingAmountCount > 0 && (
                            <span className={`${shell.stat} ${shell.statWarning}`}>
                                <WarningAmberIcon />
                                {t('MissingSellAmount')}: <strong>{missingAmountCount}</strong>
                            </span>
                        )}
                    </div>
                </div>
            )}
            footerStart={showSummary && activeYear != null ? (
                <>
                    <button
                        type="button"
                        className={shell.primaryBtn}
                        onClick={handleSaveYear}
                        disabled={saving || yearAssets.length === 0 || savedInYearCount === yearAssets.length}
                    >
                        {saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                        {t('CapitalGainSaveYear', { year: activeYear }) || `שמור תמונת מצב לשנת ${activeYear}`}
                    </button>
                    <button
                        type="button"
                        className={shell.ghostBtn}
                        onClick={handleResetYear}
                        disabled={saving || savedInYearCount === 0}
                    >
                        <RestartAltIcon />
                        {t('CapitalGainResetToComputed') || 'אפס לחישוב'}
                    </button>
                </>
            ) : null}
            footer={(
                <button type="button" className={shell.secondaryBtn} onClick={onClose}>
                    {t('Close') || 'סגור'}
                </button>
            )}
        >
            <div className={shell.tableWrap}>
                <TableContainer sx={{ maxHeight: '55vh', overflowX: 'hidden' }}>
                    <Table stickyHeader size="small" dir={isRtl ? 'rtl' : 'ltr'} className={`${styles.table} ${styles.table}`}>
                        <colgroup>
                            {columns.map((col) => (
                                <col key={col.id} style={{ width: col.width }} />
                            ))}
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableCell key={col.id} align="center">
                                        {col.sortable === false ? (
                                            col.label
                                        ) : (
                                            <TableSortLabel
                                                active={orderBy === col.id}
                                                direction={orderBy === col.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(col.id)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.map((asset) => {
                                const children = rootToChildren[asset.id] || [];
                                const hasChildren = children.length > 0;
                                const isMissing =
                                    !asset.soldViaAssetGroup &&
                                    (!asset.sellAmount || asset.sellAmount === 0);

                                const rootBg = isMissing
                                    ? '#fff5f5'
                                    : hasChildren
                                      ? SOLD_ASSET_ROOT_GROUP_BG
                                      : '#fff';

                                return (
                                    <React.Fragment key={asset.id}>
                                        <TableRow
                                            hover={!isMissing}
                                            onClick={() => onSelectAsset(asset)}
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor: rootBg,
                                                ...(hasChildren && {
                                                    borderInlineStart: '3px solid var(--xtram-blue)',
                                                }),
                                                '&:hover': {
                                                    backgroundColor: isMissing ? '#fdeaea' : SOLD_ASSET_ROW_HOVER_BG,
                                                },
                                            }}
                                        >
                                            <SoldAssetTableCells
                                                asset={asset}
                                                groupMap={groupMap}
                                                t={t}
                                                isChildRow={false}
                                                isRtl={isRtl}
                                                summary={summaries[asset.id]}
                                                showSummary={showSummary}
                                            />
                                        </TableRow>
                                        {children.map((child) => (
                                            <TableRow
                                                key={`child-${child.id}`}
                                                sx={{
                                                    backgroundColor: SOLD_ASSET_CHILD_ROW_BG,
                                                    cursor: 'default',
                                                    borderInlineStart: '3px solid rgba(48, 79, 255, 0.2)',
                                                    '&:hover': {
                                                        backgroundColor: SOLD_ASSET_CHILD_ROW_BG,
                                                    },
                                                }}
                                            >
                                                <SoldAssetTableCells
                                                    asset={child}
                                                    groupMap={groupMap}
                                                    t={t}
                                                    isChildRow
                                                    isRtl={isRtl}
                                                />
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                            {paginated.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columns.length}>
                                        <div className={shell.emptyState}>{t('NoSoldAssets') || 'אין נכסים שנמכרו'}</div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    className={shell.pagination}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={sorted.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage={t('RowsPerPage')}
                />
            </div>
        </AssetModalShell>
    );
};

export default CapitalGainSoldAssetsModal;
